/**
 * @file index.tsx
 *
 * @fileoverview Renders a hero screen with a product screenshot on the left.
 */
import Image from '~/jobiqo/components/Image';
import { HeroSplitWithScreenParagraph } from '~/jobiqo/types/types';
import { Badge } from '~/vcl/atoms/03-badge';
import { FlexItems } from '~/vcl/atoms/06-layout/03-flex';
import { FlexItem } from '~/vcl/atoms/06-layout/03-flex/02-flex-item';
import { Grid } from '~/vcl/atoms/06-layout/04-grid';
import { Cell } from '~/vcl/atoms/06-layout/04-grid/01-cell';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { Main } from '~/vcl/atoms/06-layout/09-main';
import { Box } from '~/vcl/atoms/07-box';
import { Text } from '~/vcl/atoms/13-text/03-text';
import styles from './heroSplitWithScreen.module.scss';
import { getFrontendTheme } from '~/jobiqo/utils/getFrontend';
import { cva } from 'class-variance-authority';
import React, { useEffect, useState } from 'react';
import { useIsMobile } from '~/vcl/hooks/useIsMobile';
import Link from 'next/link';
import { getFormattedPageLink } from '~/jobiqo/utils/routes';
import { JobSearchForm } from '~/components/Form/SearchInputForm';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';

const ImageWrapper = cva([styles.sideWrapperInner], {
  variants: {
    left: {
      true: styles.sideWrapperInnerLeft,
      false: styles.sideWrapperInnerRight,
    },
    shadow: {
      true: styles.shadow,
    },
  },
});

interface HeroSplitWithScreenProps {
  /**
   * The row to display.
   */
  row: HeroSplitWithScreenParagraph;
  /**
   * The index of this row. Used to detect if its the first row on the page.
   */
  index: number;
  /**
   * The max container size.
   */
  maxContainerSize?: 'small' | 'medium' | 'large' | 'xLarge';
}
export function HeroSplitWithScreen({
  row: {
    screenshotPosition,
    spacing,
    background,
    tagText,
    color,
    subText,
    h1,
    text,
    bottomText,
    bottomTextLink,
    screenshot,
    includeSearch,
  },
  index,

  maxContainerSize,
}: HeroSplitWithScreenProps) {
  const isScreenshootLeft = screenshotPosition === 'SCREENSHOT_POSITION_LEFT';
  const frontendConfig = useFrontendConfig();
  const frontendTheme = getFrontendTheme(frontendConfig);

  // Add a useEffect that will calculate a size for the jcl-hero-split-screen-content-wrapper based on the
  // size of the image jcl-lp-hero-split-screen-image-wrapper
  const [size, setSize] = useState<null | number>(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    function updateSize() {
      const image = document.querySelector(
        '.jcl-lp-hero-split-screen-image-wrapper img',
      );
      if (image) {
        const imageHeight = image.clientHeight;
        setSize(imageHeight);
      }
    }

    if (!isMobile) {
      // Update size on mount and resize
      updateSize();
      window.addEventListener('resize', updateSize);

      // Cleanup event listener on unmount
      return () => window.removeEventListener('resize', updateSize);
    }

    return () => {};
  }, [isMobile]);

  return (
    <Box
      boxShadow="none"
      borderRadius="0px"
      marginTop={0}
      paddingTop={[5, spacing ?? 12]}
      paddingBottom={[5, spacing ?? 11]}
      backgroundColorOverride={
        background ? `var(--color-${background})` : `var(--color-white)`
      }
      className={`${styles.wrapper} jd-lp-hero-split-screen`}
    >
      <div
        className="jcl-hero-split-screen-content-wrapper"
        style={{
          minHeight: size ? `${size}px` : 'auto',
        }}
      >
        <FlexItems flexDirection="column">
          <FlexItem
            className={`${styles.sideWrapper} ${
              isScreenshootLeft
                ? styles.sideWrapperLeft
                : styles.sideWrapperRight
            }`}
          >
            {frontendTheme.LANDING_PAGES.HERO_SPLIT_SCREEN.PATTERN && (
              <svg
                className={`${styles.svgDots} ${
                  isScreenshootLeft ? styles.svgDotsLeft : styles.svgDotsRight
                } jd-lp-hero-split-pattern`}
                width="404"
                height="392"
                fill="none"
                viewBox="0 0 404 392"
              >
                <defs>
                  <pattern
                    id="837c3e70-6c3a-44e6-8854-cc48c737b659"
                    x="0"
                    y="0"
                    width="20"
                    height="20"
                    patternUnits="userSpaceOnUse"
                  >
                    <rect
                      x="0"
                      y="0"
                      width="4"
                      height="4"
                      className="text-gray-200"
                      fill="currentColor"
                    ></rect>
                  </pattern>
                </defs>
                <rect
                  width="404"
                  height="392"
                  fill="url(#837c3e70-6c3a-44e6-8854-cc48c737b659)"
                ></rect>
              </svg>
            )}
            <div
              className={`${ImageWrapper({
                left: isScreenshootLeft,
                shadow: frontendTheme.LANDING_PAGES.HERO_SPLIT_SCREEN.SHADOW,
              })} jcl-lp-hero-split-screen-image-wrapper`}
            >
              <Image
                src={screenshot.url}
                alt={screenshot.alt ?? ''}
                width={screenshot.width}
                height={screenshot.height}
                priority={index === 0}
                {...(screenshot.caption && {
                  caption: screenshot.caption,
                })}
              />
            </div>
          </FlexItem>
          <FlexItem className={styles.contentWrapper}>
            <Main {...(maxContainerSize ? { size: maxContainerSize } : null)}>
              <Spacing
                marginTop={
                  frontendTheme.LANDING_PAGES.HERO_SPLIT_SCREEN.CONTENT_MARGIN
                }
              >
                <Grid columns={[1, 2]}>
                  {isScreenshootLeft && <Cell />}
                  <Cell className={styles.content}>
                    <FlexItems alignItems="center" gap={4}>
                      {tagText && (
                        <FlexItem>
                          <Badge badgeStyle={`${color}VeryLight` as any}>
                            {tagText}
                          </Badge>
                        </FlexItem>
                      )}
                      {subText && (
                        <FlexItem>
                          <Text color={color as any} tag="span">
                            {subText}
                          </Text>
                        </FlexItem>
                      )}
                    </FlexItems>
                    <Spacing size="large" />
                    <Text
                      className="jd-lp-hero-split-heading"
                      tag={index === 0 ? 'h1' : 'h2'}
                      fontWeight={
                        frontendTheme.LANDING_PAGES.HERO_SPLIT_SCREEN
                          .HEADING_FONT_WEIGHT
                      }
                      fontSize={index === 0 ? [6, 8] : [5, 7]}
                      lineHeight={index === 0 ? [6, 7] : [5, 7]}
                    >
                      {h1}
                    </Text>
                    <Spacing size="large" />
                    {text && (
                      <>
                        <Text color="gray6" fontSize={4} lineHeight={5}>
                          {text}
                        </Text>
                        <Spacing size="large" />
                      </>
                    )}
                    {(bottomText || bottomTextLink) &&
                      (bottomTextLink && bottomTextLink?.url ? (
                        <Link
                          {...getFormattedPageLink(bottomTextLink?.url)}
                          style={{ color }}
                        >
                          <Text tag="span" fontWeight="bold">
                            {bottomTextLink.text}
                          </Text>
                        </Link>
                      ) : (
                        <Text color={color as any} tag="span" fontWeight="bold">
                          {bottomText}
                        </Text>
                      ))}
                    {includeSearch && (
                      <Spacing size="large">
                        <JobSearchForm />
                      </Spacing>
                    )}
                  </Cell>
                  {!isScreenshootLeft && <Cell />}
                </Grid>
              </Spacing>
            </Main>
          </FlexItem>
        </FlexItems>
      </div>
    </Box>
  );
}
export default HeroSplitWithScreen;
